/**
 * Design tokens for inline styles. Colours and shadows are CSS variables
 * defined in styles.css (light, dark and follow-system values live there),
 * so components keep writing `theme.color.border` and pick up the active
 * theme. Use only these, never hex literals, so both themes stay correct.
 *
 * Variables only work as CSS values: an SVG presentation attribute
 * (stroke=, fill=) can't take one, so icons take colour through `color`.
 */
export const theme = {
  color: {
    // Neutrals
    bg: "var(--bg)",
    surface: "var(--surface)",
    surfaceSubtle: "var(--surface-2)",
    surfaceHover: "var(--surface-hover)",
    border: "var(--border)",
    borderStrong: "var(--border-strong)",
    text: "var(--text)",
    textMuted: "var(--text-muted)",
    textFaint: "var(--text-faint)",

    // Sidebar (dark navy in both themes)
    sidebarBg: "var(--sidebar-bg)",
    sidebarText: "var(--sidebar-text)",
    sidebarTextMuted: "var(--sidebar-text-muted)",
    sidebarActiveBg: "var(--sidebar-active-bg)",

    // Brand: navy for actions and links; orange accent for marks and highlights only
    brand: "var(--brand)",
    brandHover: "var(--brand-hover)",
    brandInk: "var(--brand-ink)",
    brandFaint: "var(--brand-faint)",
    brandBorder: "var(--brand-border)",
    onBrand: "var(--on-brand)",
    accent: "var(--accent)",
    accentInk: "var(--accent-ink)",
    accentFaint: "var(--accent-faint)",

    // Status: base (dots, bars, icons), faint fill, text on the fill, border on the fill
    success: "var(--success)",
    successFaint: "var(--success-faint)",
    successText: "var(--success-text)",
    successBorder: "var(--success-border)",
    warning: "var(--warning)",
    warningFaint: "var(--warning-faint)",
    warningText: "var(--warning-text)",
    warningBorder: "var(--warning-border)",
    danger: "var(--danger)",
    dangerFaint: "var(--danger-faint)",
    dangerText: "var(--danger-text)",
    dangerBorder: "var(--danger-border)",

    scrim: "var(--scrim)",
  },
  space: { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 },
  radius: { sm: 4, md: 6, lg: 8, xl: 12 },
  shadow: {
    sm: "var(--shadow-sm)",
    md: "var(--shadow-md)",
    lg: "var(--shadow-lg)",
  },
  font: {
    stack: "var(--font-sans)",
    mono: "var(--font-mono)",
    size: { xs: 11, sm: 12, base: 13, md: 14, lg: 16, xl: 20, xxl: 28 },
    weight: { regular: 400, medium: 500, semibold: 600, bold: 700 },
  },
} as const;
